// src/features/executive/components/ExecutivePermissionSection/ExecutivePermissionSection.tsx
import { Box } from '@mui/material'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import InputCheckbox from 'component/Input/InputCheckbox'
import RadioButton from 'new-components/buttons/radio-button'
import type { UserExecutivePermissions } from 'api/executive/executive.types'

interface Props {
  value: UserExecutivePermissions
  onChange: (next: UserExecutivePermissions) => void
}

const SAFE_DEFAULTS: UserExecutivePermissions = {
  executiveDashboardTabs: { performance: false, account: false, sales: false, consumerInsight: false },
  executiveDashboardScope: 'own-branch',
  canManageDashboardTargets: false,
}

export default function ExecutivePermissionSection({ value, onChange }: Props) {
  const { t } = useTranslation()

  // Backend may return a partial shape (e.g. only tabs, no scope). Merge with
  // defaults so nested reads never produce undefined and inputs stay controlled.
  const safe: UserExecutivePermissions = {
    executiveDashboardTabs: { ...SAFE_DEFAULTS.executiveDashboardTabs, ...(value?.executiveDashboardTabs ?? {}) },
    executiveDashboardScope: value?.executiveDashboardScope ?? SAFE_DEFAULTS.executiveDashboardScope,
    canManageDashboardTargets: value?.canManageDashboardTargets ?? SAFE_DEFAULTS.canManageDashboardTargets,
  }

  const setTab = (key: keyof UserExecutivePermissions['executiveDashboardTabs'], checked: boolean) => {
    onChange({
      ...safe,
      executiveDashboardTabs: { ...safe.executiveDashboardTabs, [key]: checked },
    })
  }

  return (
    <Col sm={12} className="px-2">
      <Row>
        <Col sm={6} className="px-2 mt-2">
          <InputCheckbox
            label={t('EXECUTIVE.TAB.PERFORMANCE')}
            checked={safe.executiveDashboardTabs.performance || false}
            onChange={(e) => setTab('performance', e.target.checked)}
          />
        </Col>
        <Col sm={6} className="px-2 mt-2">
          <InputCheckbox
            label={t('EXECUTIVE.TAB.ACCOUNT')}
            checked={safe.executiveDashboardTabs.account || false}
            onChange={(e) => setTab('account', e.target.checked)}
          />
        </Col>
        <Col sm={6} className="px-2 mt-2">
          <InputCheckbox
            label={t('EXECUTIVE.TAB.SALES')}
            checked={safe.executiveDashboardTabs.sales || false}
            onChange={(e) => setTab('sales', e.target.checked)}
          />
        </Col>
        <Col sm={6} className="px-2 mt-2">
          <InputCheckbox
            label={t('EXECUTIVE.TAB.INSIGHT')}
            checked={safe.executiveDashboardTabs.consumerInsight || false}
            onChange={(e) => setTab('consumerInsight', e.target.checked)}
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col sm={12} className="px-2">
          <Box sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 0.5 }}>
            {t('EXECUTIVE.PERMISSION_SECTION.SCOPE_TITLE')}
          </Box>
          <RadioButton
            row
            name="executive-scope"
            value={safe.executiveDashboardScope}
            onChange={(e) =>
              onChange({
                ...safe,
                executiveDashboardScope: e.target.value as 'own-branch' | 'all-branches',
              })
            }
            options={[
              { label: t('EXECUTIVE.PERMISSION_SECTION.SCOPE_OWN_BRANCH'), value: 'own-branch' },
              { label: t('EXECUTIVE.PERMISSION_SECTION.SCOPE_ALL_BRANCHES'), value: 'all-branches' },
            ]}
          />
        </Col>
      </Row>

      <Row className="mt-2">
        <Col sm={12} className="px-2">
          <InputCheckbox
            label={t('EXECUTIVE.PERMISSION_SECTION.MANAGE_TARGETS')}
            checked={safe.canManageDashboardTargets || false}
            onChange={(e) => onChange({ ...safe, canManageDashboardTargets: e.target.checked })}
          />
        </Col>
      </Row>
    </Col>
  )
}
